/**
 * useFileUpload - Hook for managing file uploads and drag-drop
 */
import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export const useFileUpload = (caseId) => {
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Upload a single file
  const uploadFile = useCallback(async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Filen ${file.name} er for stor (maks 50MB)`);
      return null;
    }

    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Add to uploading state
    setUploadingFiles(prev => [...prev, {
      id: uploadId,
      name: file.name,
      progress: 0
    }]);

    try {
      const token = auth.getToken();
      const formData = new FormData();
      formData.append('file', file);
      if (caseId) {
        formData.append('case_id', caseId);
      }

      const response = await axios.post(
        `${API}/documents/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadingFiles(prev => prev.map(f => 
              f.id === uploadId ? { ...f, progress } : f
            ));
          }
        }
      );

      // Remove from uploading and add to attachments
      setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
      
      const attachment = {
        id: response.data.id || response.data.document_id,
        filename: file.name,
        size: file.size,
        type: file.type
      };
      
      setAttachments(prev => [...prev, attachment]);
      return attachment;
      
    } catch (error) {
      console.error('File upload error:', error);
      setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
      toast.error(`Kunne ikke laste opp ${file.name}`);
      return null;
    }
  }, [caseId]);

  // Upload multiple files
  const uploadFiles = useCallback(async (files) => {
    const results = await Promise.all(
      Array.from(files).map(file => uploadFile(file))
    );
    return results.filter(Boolean);
  }, [uploadFile]);

  // Handle file input change
  const handleFileSelect = useCallback((event) => {
    const files = event.target.files;
    if (files?.length) {
      uploadFiles(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadFiles]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files?.length) {
      await uploadFiles(files);
    }
  }, [uploadFiles]);

  // Remove an attachment
  const removeAttachment = useCallback((id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  }, []);

  // Clear all attachments
  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  // Trigger file input click
  const triggerFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    // State
    attachments,
    uploadingFiles,
    isDragging,
    fileInputRef,
    
    // Actions
    uploadFile,
    uploadFiles,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    removeAttachment,
    clearAttachments,
    triggerFileSelect,
    setAttachments
  };
};

export default useFileUpload;
